enum Products {
  DAM = "DAM",
  RTM = "RTM",
  HPDAM = "HPDAM",
  GDAM = "GDAM",

  IntraDay = "Intra-Day Contracts",
  ContigencyContracts = "Day Ahead Contingency Contracts",
  Daily = "Daily Contracts",
  Weekly = "Weekly Contracts",
  Monthly = "Monthly Contracts",
  AnyDay = "Any Day Contracts",
  SingleSided = "Any Day Single Sided Contracts",
  Bilateral = "Trading Licensees",
}

const MONTH_ABBR: Record<string, number> = {
  jan: 0,
  feb: 1,
  mar: 2,
  apr: 3,
  may: 4,
  jun: 5,
  jul: 6,
  aug: 7,
  sep: 8,
  oct: 9,
  nov: 10,
  dec: 11,
};

function parseMonthKey(month: string | null | undefined): number {
  if (!month || typeof month !== "string") return Number.MAX_SAFE_INTEGER;

  const [rawAbbr, rawYr] = month.split("-");
  if (!rawAbbr || !rawYr) return Number.MAX_SAFE_INTEGER;

  const monthIndex = MONTH_ABBR[rawAbbr.slice(0, 3).toLowerCase()];
  const year = Number(rawYr);
  if (monthIndex === undefined || Number.isNaN(year)) {
    return Number.MAX_SAFE_INTEGER;
  }

  const fullYear = rawYr.length <= 2 ? 2000 + year : year;
  return fullYear * 12 + monthIndex;
}

function sortByMonth<T extends { month?: string | null }>(arr: T[]): T[] {
  return arr.sort((a, b) => parseMonthKey(a.month) - parseMonthKey(b.month));
}

const PRODUCT_TO_KEY: Record<string, keyof MarketMonitoringData> = {
  [Products.DAM]: "dam",
  [Products.RTM]: "rtm",
  [Products.HPDAM]: "hpdam",
  [Products.GDAM]: "gdam",
  [Products.IntraDay]: "intraDay",
  [Products.ContigencyContracts]: "contingencyContracts",
  [Products.Daily]: "daily",
  [Products.Weekly]: "weekly",
  [Products.Monthly]: "monthly",
  [Products.AnyDay]: "anyDay",
  [Products.SingleSided]: "singleSided",
  [Products.Bilateral]: "bilateral",
};

export interface MarketMonitoringItem {
  month: string;
  product: Products;
  value: number;
  totalVolume?: number;
  totalCost?: number;
}

export interface MarketMonitoringData {
  dam: MarketMonitoringItem[];
  rtm: MarketMonitoringItem[];
  hpdam: MarketMonitoringItem[];
  gdam: MarketMonitoringItem[];
  intraDay: MarketMonitoringItem[];
  contingencyContracts: MarketMonitoringItem[];
  daily: MarketMonitoringItem[];
  weekly: MarketMonitoringItem[];
  monthly: MarketMonitoringItem[];
  anyDay: MarketMonitoringItem[];
  singleSided: MarketMonitoringItem[];
  bilateral: MarketMonitoringItem[];
}

export interface ChartExchangeItem {
  month: string;
  dam: number;
  rtm: number;
  hpdam: number;
  gdam: number;
  intraDay: number;
  contingencyContracts: number;
  daily: number;
  weekly: number;
  monthly: number;
  anyDay: number;
  singleSided: number;
  bilateral: number;
  [key: string]: number | string | null;
}

export function FormatMarketMonitoringData(
  data: any,
  includingExchange?: String[],
  includingMarket?: String[],
  isWeighted?: boolean,
): ChartExchangeItem[] {
  const formattedDataArray: MarketMonitoringData = {
    dam: [],
    rtm: [],
    hpdam: [],
    gdam: [],
    intraDay: [],
    contingencyContracts: [],
    daily: [],
    weekly: [],
    monthly: [],
    anyDay: [],
    singleSided: [],
    bilateral: [],
  };

  const updateFormattedData = (
    dataArray: MarketMonitoringItem[],
    product: Products,
    item: any,
  ) => {
    const index = dataArray.findIndex(
      (element) => element.month === item.month,
    );

    if (index === -1) {
      var toPush: MarketMonitoringItem = {
        month: item.month,
        product: product,
        value: item.value,
      };
      if (isWeighted) {
        toPush.totalVolume = item.volume;
        toPush.totalCost = item.value * item.volume;
      }

      dataArray.push(toPush);
    } else {
      if (isWeighted) {
        dataArray[index].totalVolume += item.volume;
        dataArray[index].totalCost! += item.value * item.volume;
        dataArray[index].value =
          dataArray[index].totalVolume === 0
            ? 0
            : dataArray[index].totalCost! / dataArray[index].totalVolume!;
      } else {
        dataArray[index].value += item.value;
      }
    }
  };
  Object.keys(data).forEach((key) => {
    if (!includingExchange?.includes(key)) return;
    Object.keys(data[key]).forEach((key2) => {
      if (!includingMarket?.includes(key2)) return;
      data[key][key2].forEach((item: any) => {
        const dataKey = PRODUCT_TO_KEY[item.product];
        if (dataKey) {
          updateFormattedData(formattedDataArray[dataKey], item.product, item);
        }
      });
    });
  });
  // Sort each product array by month
  const keys = Object.keys(
    formattedDataArray,
  ) as (keyof MarketMonitoringData)[];
  for (const key of keys) {
    sortByMonth(formattedDataArray[key]);
  }

  const monthBuckets = {
    dam: new Map(formattedDataArray.dam.map((item) => [item.month, item])),
    rtm: new Map(formattedDataArray.rtm.map((item) => [item.month, item])),
    hpdam: new Map(formattedDataArray.hpdam.map((item) => [item.month, item])),
    gdam: new Map(formattedDataArray.gdam.map((item) => [item.month, item])),
    intraDay: new Map(
      formattedDataArray.intraDay.map((item) => [item.month, item]),
    ),
    contingencyContracts: new Map(
      formattedDataArray.contingencyContracts.map((item) => [item.month, item]),
    ),
    daily: new Map(formattedDataArray.daily.map((item) => [item.month, item])),
    weekly: new Map(
      formattedDataArray.weekly.map((item) => [item.month, item]),
    ),
    monthly: new Map(
      formattedDataArray.monthly.map((item) => [item.month, item]),
    ),
    anyDay: new Map(
      formattedDataArray.anyDay.map((item) => [item.month, item]),
    ),
    singleSided: new Map(
      formattedDataArray.singleSided.map((item) => [item.month, item]),
    ),
    bilateral: new Map(
      formattedDataArray.bilateral.map((item) => [item.month, item]),
    ),
  };

  const sortedMonths = Array.from(
    new Set(
      keys.flatMap((key) => formattedDataArray[key].map((item) => item.month)),
    ),
  ).sort((a, b) => parseMonthKey(a) - parseMonthKey(b));

  const finalChartExchangeData: ChartExchangeItem[] = sortedMonths.map(
    (month) => {
      const entry: ChartExchangeItem = {
        month,
        dam: 0,
        rtm: 0,
        hpdam: 0,
        gdam: 0,
        intraDay: 0,
        contingencyContracts: 0,
        daily: 0,
        weekly: 0,
        monthly: 0,
        anyDay: 0,
        singleSided: 0,
        bilateral: 0,
      };

      for (const key of keys) {
        const monthData = monthBuckets[key].get(month);
        entry[key] = monthData?.value ?? 0;
        entry[`${key}_volume`] = monthData?.totalVolume ?? null;
      }

      return entry;
    },
  );

  return finalChartExchangeData;
}

enum Exchange {
  IEX = "IEX",
  HPX = "HPX",
  PXIL = "PXIL",
  Traders = "Traders",
}

export interface ChartByPriceItem {
  month: string;
  iex: number;
  hpx: number;
  pxil: number;
  traders: number;
  [key: string]: number | string | null;
}

export interface ChartMarketProductItem {
  month: string;
  [key: string]: number | string | null;
}

export interface MarketProductSeries {
  name: string;
  priceKey: string;
  volumeKey: string;
  exchange: string;
  market: string;
  product: string;
}

interface ByPriceItem {
  value: number;
  exchange: Exchange;
  month: string;
  cost?: number;
  totalVolume?: number;
}

interface ByPriceData {
  iex: ByPriceItem[];
  hpx: ByPriceItem[];
  pxil: ByPriceItem[];
  traders: ByPriceItem[];
}

function toMarketProductKey(
  exchange: string,
  market: string,
  product: string,
): string {
  return `${exchange}__${market}__${product}`
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

export function FormatComparativeByMarketProductData(
  data: any,
  selectedProducts: string[] = [],
  selectedMarkets: string[] = [],
  productDisplayMap: Record<string, string> = {},
  selectedExchanges: string[] = [],
): {
  rows: ChartMarketProductItem[];
  series: MarketProductSeries[];
} {
  const rowMap = new Map<string, ChartMarketProductItem>();
  const seriesMap = new Map<string, MarketProductSeries>();

  Object.keys(data ?? {}).forEach((exchange) => {
    if (selectedExchanges.length > 0 && !selectedExchanges.includes(exchange)) {
      return;
    }

    Object.keys(data[exchange] ?? {}).forEach((market) => {
      if (selectedMarkets.length > 0 && !selectedMarkets.includes(market)) {
        return;
      }

      const marketRows = data[exchange][market];
      if (!Array.isArray(marketRows)) {
        return;
      }

      marketRows.forEach((item: any) => {
        const productName = String(item.product ?? "");
        if (
          selectedProducts.length > 0 &&
          !selectedProducts.includes(productName)
        ) {
          return;
        }

        const displayProduct =
          productDisplayMap[productName] ?? productName ?? "Unknown";
        const month = String(item.month ?? "");
        if (!month) {
          return;
        }

        const combinationKey = toMarketProductKey(
          exchange,
          market,
          displayProduct,
        );
        const priceKey = `${combinationKey}_price`;
        const volumeKey = `${combinationKey}_volume`;

        if (!seriesMap.has(combinationKey)) {
          seriesMap.set(combinationKey, {
            name: `${market} - ${displayProduct} (${exchange})`,
            priceKey,
            volumeKey,
            exchange,
            market,
            product: productName,
          });
        }

        const rawPrice = Number(item.value);
        const rawVolume = Number(item.volume);

        const row = rowMap.get(month) ?? { month };
        row[priceKey] = Number.isFinite(rawPrice) ? rawPrice : null;
        row[volumeKey] = Number.isFinite(rawVolume) ? rawVolume : null;
        rowMap.set(month, row);
      });
    });
  });

  const marketOrder = new Map(
    selectedMarkets.map((market, idx) => [market, idx]),
  );
  const productOrder = new Map(
    selectedProducts.map((product, idx) => [product, idx]),
  );
  const exchangeOrder = new Map(
    selectedExchanges.map((exchange, idx) => [exchange, idx]),
  );

  const orderedSeries = Array.from(seriesMap.values()).sort((a, b) => {
    const marketIdxA = marketOrder.get(a.market) ?? Number.MAX_SAFE_INTEGER;
    const marketIdxB = marketOrder.get(b.market) ?? Number.MAX_SAFE_INTEGER;
    if (marketIdxA !== marketIdxB) {
      return marketIdxA - marketIdxB;
    }

    const productIdxA = productOrder.get(a.product) ?? Number.MAX_SAFE_INTEGER;
    const productIdxB = productOrder.get(b.product) ?? Number.MAX_SAFE_INTEGER;
    if (productIdxA !== productIdxB) {
      return productIdxA - productIdxB;
    }

    const exchangeIdxA =
      exchangeOrder.get(a.exchange) ?? Number.MAX_SAFE_INTEGER;
    const exchangeIdxB =
      exchangeOrder.get(b.exchange) ?? Number.MAX_SAFE_INTEGER;
    if (exchangeIdxA !== exchangeIdxB) {
      return exchangeIdxA - exchangeIdxB;
    }

    return a.name.localeCompare(b.name);
  });

  const sortedMonths = Array.from(rowMap.keys()).sort(
    (a, b) => parseMonthKey(a) - parseMonthKey(b),
  );

  const rows: ChartMarketProductItem[] = sortedMonths.map((month) => {
    const row = rowMap.get(month) ?? { month };

    orderedSeries.forEach((series) => {
      if (!(series.priceKey in row)) {
        row[series.priceKey] = null;
      }
      if (!(series.volumeKey in row)) {
        row[series.volumeKey] = null;
      }
    });

    return row;
  });

  return {
    rows,
    series: orderedSeries,
  };
}

export function FormatByPriceData(
  data: any,
  selectedProduct?: String[],
  selectedMarket?: String[],
  isPrice?: boolean,
): ChartByPriceItem[] {
  const formattedDataArray: ByPriceData = {
    iex: [],
    hpx: [],
    pxil: [],
    traders: [],
  };

  const updateFormattedData = (
    dataArray: ByPriceItem[],
    exchange: Exchange,
    item: any,
  ) => {
    const index = dataArray.findIndex(
      (element) => element.month === item.month,
    );
    if (!selectedProduct?.includes(item.product)) {
      return;
    } else {
      if (index === -1) {
        var toBePushed: ByPriceItem = {
          month: item.month,
          exchange: exchange,
          value: parseFloat(item.value),
        };
        if (isPrice) {
          toBePushed.cost = item.value * item.volume;
          toBePushed.totalVolume = item.volume;
        }
        dataArray.push(toBePushed);
      } else {
        dataArray[index].value += parseFloat(item.value);
        if (isPrice) {
          dataArray[index].cost! += item.value * item.volume;
          dataArray[index].totalVolume! += item.volume;
          dataArray[index].value =
            dataArray[index].totalVolume === 0
              ? 0
              : dataArray[index].cost! / dataArray[index].totalVolume!;
        }
      }
    }
  };

  const EXCHANGE_TO_KEY: Record<string, keyof ByPriceData> = {
    [Exchange.IEX]: "iex",
    [Exchange.HPX]: "hpx",
    [Exchange.PXIL]: "pxil",
    [Exchange.Traders]: "traders",
  };

  Object.keys(data).forEach((exchange) => {
    Object.keys(data[exchange]).forEach((market) => {
      if (!selectedMarket?.includes(market)) return;
      const dataKey = EXCHANGE_TO_KEY[exchange];
      if (dataKey) {
        data[exchange][market].forEach((item: any) => {
          updateFormattedData(
            formattedDataArray[dataKey],
            exchange as Exchange,
            item,
          );
        });
      }
    });
  });
  // Sort each exchange array by month
  const keys = Object.keys(formattedDataArray) as (keyof ByPriceData)[];
  for (const key of keys) {
    sortByMonth(formattedDataArray[key]);
  }

  const monthBuckets = {
    iex: new Map(formattedDataArray.iex.map((item) => [item.month, item])),
    hpx: new Map(formattedDataArray.hpx.map((item) => [item.month, item])),
    pxil: new Map(formattedDataArray.pxil.map((item) => [item.month, item])),
    traders: new Map(
      formattedDataArray.traders.map((item) => [item.month, item]),
    ),
  };

  const sortedMonths = Array.from(
    new Set(
      keys.flatMap((key) => formattedDataArray[key].map((item) => item.month)),
    ),
  ).sort((a, b) => parseMonthKey(a) - parseMonthKey(b));

  const finalChartByPriceData: ChartByPriceItem[] = sortedMonths.map(
    (month) => {
      const entry: ChartByPriceItem = {
        month,
        iex: 0,
        hpx: 0,
        pxil: 0,
        traders: 0,
      };

      for (const key of keys) {
        const monthData = monthBuckets[key].get(month);
        entry[key] = monthData?.value ?? 0;
        entry[`${key}_volume`] = monthData?.totalVolume ?? null;
      }

      return entry;
    },
  );

  return finalChartByPriceData;
}
